import type { ReactNode } from 'react';
import { CheckCircle2, Circle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fmtKg } from '../../lib/saldo';

// Badge stato registrazione Ecomatico (#89). Tooltip con data+utente sul "Registrato".
export function EcomaticoBadge({ registrato, at, daNome }: {
  registrato: boolean; at: string | null; daNome: string | null;
}) {
  const { t } = useTranslation();
  if (registrato) {
    const quando = at ? new Date(at).toLocaleString('it-IT') : '';
    const title = `${t('magazzino.ecomatico.registrato')}${quando ? ` ${t('magazzino.ecomatico.title_il', { quando })}` : ''}${daNome ? ` ${t('magazzino.ecomatico.title_da', { nome: daNome })}` : ''}`;
    return (
      <span title={title} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
        <CheckCircle2 size={12} /> {t('magazzino.ecomatico.registrato')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
      <Circle size={12} /> {t('magazzino.ecomatico.da_registrare')}
    </span>
  );
}

// Filtro segmented Tutti / Da registrare / Registrati. undefined = tutti.
export function EcomaticoFiltro({ value, onChange }: {
  value: boolean | undefined; onChange: (v: boolean | undefined) => void;
}) {
  const { t } = useTranslation();
  const opts: { label: string; v: boolean | undefined }[] = [
    { label: t('magazzino.ecomatico.filtro_tutti'), v: undefined },
    { label: t('magazzino.ecomatico.filtro_da_registrare'), v: false },
    { label: t('magazzino.ecomatico.filtro_registrati'), v: true },
  ];
  return (
    <div className="inline-flex rounded-lg border border-stone-border overflow-hidden">
      {opts.map((o) => (
        <button
          key={o.label}
          type="button"
          onClick={() => onChange(o.v)}
          className={`px-3 py-1.5 text-sm font-medium transition-colors ${
            value === o.v ? 'bg-sage text-white' : 'bg-white text-ink-mid hover:bg-stone-bg'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Barra azioni bulk (reversibile) quando ≥1 riga selezionata.
// `children` = il riepilogo di COSA si è selezionato (chi trascrive su Ecomatico
// oggi somma a mano): sta sotto i bottoni, non li sposta.
export function EcomaticoBulkBar({ count, busy, onRegistra, onDaRegistrare, children }: {
  count: number; busy: boolean; onRegistra: () => void; onDaRegistrare: () => void;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="mb-3 px-3 py-2 bg-sage/5 border border-sage/30 rounded-lg">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-ink-dark">{t('magazzino.ecomatico.selezionati', { count })}</span>
        <button type="button" onClick={onRegistra} disabled={busy}
          className="text-sm font-medium px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
          {t('magazzino.ecomatico.segna_registrati', { count })}
        </button>
        <button type="button" onClick={onDaRegistrare} disabled={busy}
          className="text-sm font-medium px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50">
          {t('magazzino.ecomatico.segna_da_registrare', { count })}
        </button>
      </div>
      {children}
    </div>
  );
}

type Voce = { chiave: string; kg: number | string };

/** Riepilogo di una selezione multipla: una riga per chiave coi kg (e, se
 *  `mostraN`, quante operazioni la compongono), più il totale.
 *  La somma è client-side sulle righe già in pagina: nessun endpoint, nessuna query. */
export function RiepilogoSelezione({ titolo, voci, mostraN, testid }: {
  titolo: string; voci: Voce[]; mostraN?: boolean; testid: string;
}) {
  const { t } = useTranslation();
  if (voci.length === 0) return null;
  const per = new Map<string, { kg: number; n: number }>();
  for (const v of voci) {
    const cur = per.get(v.chiave) ?? { kg: 0, n: 0 };
    per.set(v.chiave, { kg: cur.kg + Number(v.kg), n: cur.n + 1 });
  }
  const righe = [...per.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  const totale = righe.reduce((s, [, v]) => s + v.kg, 0);
  return (
    <div data-testid={testid} className="mt-2 text-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-ink-soft mb-1">{titolo}</div>
      <ul>
        {righe.map(([chiave, v]) => (
          <li key={chiave} className="flex flex-wrap gap-x-2 text-ink-mid">
            <span className="font-medium text-ink-dark">{chiave}</span>
            <span>{t('magazzino.ecomatico.riepilogo_kg', { kg: fmtKg(v.kg) })}</span>
            {mostraN && <span className="text-ink-soft">{t('magazzino.ecomatico.riepilogo_operazioni', { count: v.n })}</span>}
          </li>
        ))}
      </ul>
      <div className="mt-1 font-medium text-ink-dark">
        {t('magazzino.ecomatico.riepilogo_totale', { kg: fmtKg(totale) })}
      </div>
    </div>
  );
}
